import React, { useState } from "react";
import { useAuth } from "./AuthContext";
import "./Search.css";

// ─── University Configuration ───
const UNIVERSITIES = {
  berkeley: {
    name: "UC Berkeley",
    emoji: "🐻",
    color: "#003262",
    headerClass: "berkeley-header",
    tabClass: "",
    cardClass: "orcid-person",
    apiPath: "/api/berkeley/unified-search",
    description: "Unified search combining Berkeley Faculty Expertise + ORCID database",
    searchHint: "Search UC Berkeley faculty (name, keyword, department, or leave empty for all)...",
    source: "openalex+orcid"
  },
  imperial: {
    name: "Imperial College London",
    emoji: "🎓",
    color: "#003E74",
    headerClass: "imperial-header",
    tabClass: "imperial-tab",
    cardClass: "imperial-person openalex-card",
    apiPath: "/api/imperial-openalex/unified-search",
    description: "OpenAlex search with citations, h-index & research topics",
    searchHint: "Search Imperial College faculty (name, keyword, department, or leave empty for all)...",
    source: "openalex"
  },
  anu: {
    name: "Australian National University",
    emoji: "🦘",
    color: "#BE830E",
    headerClass: "anu-header",
    tabClass: "anu-tab",
    cardClass: "anu-person openalex-card",
    apiPath: "/api/anu-openalex/unified-search",
    description: "OpenAlex search with citations, h-index & research topics",
    searchHint: "Search ANU faculty (name, keyword, department, or leave empty for all)...",
    source: "openalex"
  },
  wits: {
    name: "University of the Witwatersrand",
    emoji: "🇿🇦",
    color: "#003B5C",
    headerClass: "wits-header",
    tabClass: "wits-tab",
    cardClass: "wits-person openalex-card",
    apiPath: "/api/wits-openalex/unified-search",
    description: "OpenAlex search with citations, h-index & research topics",
    searchHint: "Search Wits faculty (name, keyword, department, or leave empty for all)...",
    source: "openalex"
  },
  ubc: {
    name: "University of British Columbia",
    emoji: "🍁",
    color: "#002145",
    headerClass: "ubc-header",
    tabClass: "ubc-tab",
    cardClass: "ubc-person openalex-card",
    apiPath: "/api/ubc-openalex/unified-search",
    description: "OpenAlex search with citations, h-index & research topics",
    searchHint: "Search UBC faculty (name, keyword, department, or leave empty for all)...",
    source: "openalex"
  }
};

// Reusable OpenAlex profile card
function OpenAlexProfileCard({ profile, uni, user, onSave }) {
  return (
    <div className={`person-card ${uni.cardClass}`}>
      <div className="card-header">
        <h4>
          {profile.fullName || profile.name}
          {profile.hIndex > 0 && (
            <span style={{fontSize: '12px', color: '#059669', marginLeft: '8px', background: '#d1fae5', padding: '2px 6px', borderRadius: '4px'}}>
              h-index: {profile.hIndex}
            </span>
          )}
        </h4>
        {user.role === "admin" && (
          <button onClick={() => onSave(profile)} className="save-profile-btn" title="Add to local directory">
            💾 Save
          </button>
        )}
      </div>
      
      <div className="person-details">
        {/* Metrics */}
        <div style={{display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap'}}>
          {profile.worksCount > 0 && (
            <span style={{fontSize: '13px', color: '#1e40af', background: '#dbeafe', padding: '4px 8px', borderRadius: '4px'}}>
              📚 {profile.worksCount.toLocaleString()} works
            </span>
          )}
          {profile.citedByCount > 0 && (
            <span style={{fontSize: '13px', color: '#7c2d12', background: '#fed7aa', padding: '4px 8px', borderRadius: '4px'}}>
              📊 {profile.citedByCount.toLocaleString()} citations
            </span>
          )}
          {profile.i10Index > 0 && (
            <span style={{fontSize: '13px', color: '#6b21a8', background: '#e9d5ff', padding: '4px 8px', borderRadius: '4px'}}>
              i10: {profile.i10Index}
            </span>
          )}
        </div>
        
        {/* OpenAlex Profile Link */}
        {profile.profileUrl && (
          <p style={{marginBottom: '10px'}}>
            <strong>📊 OpenAlex Profile:</strong>{' '}
            <a href={profile.profileUrl} target="_blank" rel="noopener noreferrer"
              style={{color: uni.color, textDecoration: 'underline'}}>
              View on OpenAlex →
            </a>
          </p>
        )}
        
        {/* ORCID */}
        {profile.orcidId && (
          <p style={{marginBottom: '10px'}}>
            <strong>🆔 ORCID:</strong>{' '}
            <a href={`https://orcid.org/${profile.orcidId}`} target="_blank" rel="noopener noreferrer"
              style={{color: '#a6ce39', textDecoration: 'underline'}}>
              {profile.orcidId}
            </a>
          </p>
        )}
        
        <p><strong>🎓 Affiliation:</strong> {uni.name}</p>
        
        {/* Research Areas */}
        {profile.keywords && profile.keywords.length > 0 && (
          <div style={{marginTop: '10px'}}>
            <strong>🔬 Research Areas:</strong>
            <div style={{marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
              {profile.keywords.slice(0, 6).map((keyword, idx) => (
                <span key={idx} style={{
                  fontSize: '12px', color: '#374151', background: '#f3f4f6',
                  padding: '3px 8px', borderRadius: '12px', border: '1px solid #e5e7eb'
                }}>
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Topics */}
        {profile.topics && profile.topics.length > 0 && (
          <div style={{marginTop: '10px'}}>
            <strong>🏷️ Topics:</strong>
            <p style={{marginTop: '4px', fontSize: '13px', color: '#4a5568'}}>
              {profile.topics.slice(0, 3).map(t => t.name || t).join(', ')}
            </p>
          </div>
        )}
        
        <p style={{fontSize: '11px', color: '#9ca3af', marginTop: '12px'}}>
          📍 Source: OpenAlex
        </p>
      </div>
    </div>
  );
}

export default function Search() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]); // Current tab results
  const [localResults, setLocalResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("berkeley");
  const [searchInfo, setSearchInfo] = useState({ total: 0 });
  const [error, setError] = useState("");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");

  const currentYear = new Date().getFullYear();
  const uni = UNIVERSITIES[activeTab];

  if (!user) return <p>Please log in to search the directory.</p>;

  // Generic OpenAlex / university search
  const handleUniversitySearch = async (term) => {
    setLoading(true);
    setError("");
    
    try {
      const keyword = term ? term.trim() : '';
      let url = `${uni.apiPath}?q=${encodeURIComponent(keyword)}&limit=25`;
      if (yearFrom) url += `&year_from=${yearFrom}`;
      if (yearTo) url += `&year_to=${yearTo}`;
      const res = await fetch(url, { credentials: 'include' });
      
      const data = await res.json();
      
      if (data.success) {
        setResults(data.researchers || []);
        setSearchInfo({
          total: data.stats?.total || 0,
          totalWorks: data.stats?.totalWorks || 0,
          fromBerkeley: data.stats?.fromBerkeley || 0,
          fromOrcid: data.stats?.fromOrcid || 0,
          withOrcid: data.stats?.withOrcid || data.stats?.withOrcidId || 0,
          searchMethod: data.searchInfo?.searchMethod || 'OpenAlex Search',
          keyword: keyword || 'all'
        });
      } else {
        setError(data.error || "Search failed");
        setResults([]);
      }
    } catch (err) {
      console.error("Search error:", err);
      setError("Failed to search. Please try again.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Search local database
  const handleLocalSearch = async (term) => {
    if (!term) { setLocalResults([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`/api/people?q=${encodeURIComponent(term)}`, {
        credentials: 'include',
      });
      const data = await res.json();
      setLocalResults(data);
    } catch (err) {
      setLocalResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Save profile to local database
  const saveOrcidProfile = async (profile) => {
    try {
      const personData = {
        name: profile.name,
        email: "",
        department: profile.department || profile.affiliations?.join(", ") || uni?.name || "Unknown",
        skills: profile.keywords || [],
        summary: `Researcher (${profile.orcidId || 'OpenAlex'}). ${profile.biography || 'No biography available.'}`
      };

      const res = await fetch(`/api/people`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify(personData),
      });

      if (res.ok) {
        alert(`${profile.name} has been added to your directory!`);
        if (activeTab === "local" && searchTerm) handleLocalSearch(searchTerm);
      } else {
        const error = await res.json();
        alert(`Error saving profile: ${error.error}`);
      }
    } catch (err) {
      alert("Error saving profile to database");
    }
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (activeTab === "local") {
      handleLocalSearch(searchTerm);
    } else {
      handleUniversitySearch(searchTerm);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setYearFrom("");
    setYearTo("");
    setResults([]);
    setLocalResults([]);
    setSearchInfo({ total: 0 });
    setError("");
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setResults([]);
    setLocalResults([]);
    setSearchInfo({ total: 0 });
    setError("");
  };

  // Determine header class based on active tab
  const headerClass = activeTab === "local" ? "berkeley-header" : (UNIVERSITIES[activeTab]?.headerClass || "berkeley-header");

  return (
    <div className="search-container">
      {/* Dynamic Header */}
      <div className={`search-header ${headerClass}`}>
        {activeTab === "local" ? (
          <>
            <h2>🏠 Local Directory</h2>
            <p>🔍 Search your saved researcher profiles</p>
            <p style={{fontSize: '14px', color: '#718096'}}>
              Browse and search your organization's contacts
            </p>
          </>
        ) : (
          <>
            <h2>{uni.emoji} {uni.name} Directory</h2>
            <p>🔍 {uni.description}</p>
            <p style={{fontSize: '14px', color: '#e0e0e0'}}>
              Find researchers by name, department, or research interests
            </p>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="search-tabs">
        {Object.entries(UNIVERSITIES).map(([key, u]) => (
          <button
            key={key}
            className={`tab ${activeTab === key ? `active ${u.tabClass}` : ""}`}
            onClick={() => switchTab(key)}
          >
            {u.emoji} {key === "berkeley" ? "UC Berkeley" : 
                       key === "imperial" ? "Imperial" : 
                       key === "anu" ? "ANU" : 
                       key === "wits" ? "Wits" : 
                       key === "ubc" ? "UBC" : u.name}
          </button>
        ))}
        <button
          className={`tab ${activeTab === "local" ? "active" : ""}`}
          onClick={() => switchTab("local")}
        >
          🏠 Local
        </button>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearchSubmit} className="search-form">
        <div className="search-input-container">
          <input 
            type="text"
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
            placeholder={activeTab === "local" 
              ? "Search local directory by name, department, or skills..." 
              : uni?.searchHint || "Search..."}
            className="search-input"
          />
          <div className="search-buttons">
            <button type="submit" className="search-button" disabled={loading}>
              {loading ? "🔍 Searching..." : "🔍 Search"}
            </button>
            {(searchTerm || yearFrom || yearTo) && (
              <button type="button" onClick={handleClearSearch} className="clear-button" disabled={loading}>
                ✕ Clear
              </button>
            )}
          </div>
        </div>
        
        {/* Year Filter - shown for all university tabs */}
        {activeTab !== "local" && (
          <div className="year-filter">
            <span className="year-filter-label">📅 Filter by year:</span>
            <div className="year-inputs">
              <input type="number" value={yearFrom} onChange={(e) => setYearFrom(e.target.value)}
                placeholder="From" min="1950" max={currentYear} className="year-input" />
              <span className="year-separator">—</span>
              <input type="number" value={yearTo} onChange={(e) => setYearTo(e.target.value)}
                placeholder="To" min="1950" max={currentYear} className="year-input" />
            </div>
            <div className="year-presets">
              <button type="button" className="year-preset-btn" onClick={() => { setYearFrom(String(currentYear)); setYearTo(String(currentYear)); }}>
                {currentYear}
              </button>
              <button type="button" className="year-preset-btn" onClick={() => { setYearFrom(String(currentYear - 2)); setYearTo(String(currentYear)); }}>
                Last 3 yrs
              </button>
              <button type="button" className="year-preset-btn" onClick={() => { setYearFrom(String(currentYear - 4)); setYearTo(String(currentYear)); }}>
                Last 5 yrs
              </button>
              <button type="button" className="year-preset-btn" onClick={() => { setYearFrom(String(currentYear - 9)); setYearTo(String(currentYear)); }}>
                Last 10 yrs
              </button>
              {(yearFrom || yearTo) && (
                <button type="button" className="year-preset-btn year-clear-btn" onClick={() => { setYearFrom(""); setYearTo(""); }}>
                  ✕ All years
                </button>
              )}
            </div>
          </div>
        )}
        
        {loading && (
          <div className="loading-indicator">
            {activeTab === "local" 
              ? "Searching local directory..." 
              : `🔍 Searching ${uni?.name || 'university'} researchers...`}
          </div>
        )}
        
        {error && (
          <div className="error-message" style={{color: 'red', padding: '10px', background: '#fee', borderRadius: '5px', margin: '10px 0'}}>
            ⚠️ {error}
          </div>
        )}
      </form>

      <div className="search-results">
        {/* University Results (Berkeley, Imperial, ANU, Wits, UBC) */}
        {activeTab !== "local" && results.length > 0 && (
          <div className="results-section">
            <div className={`results-header ${activeTab !== "berkeley" ? `${activeTab}-results-header` : ""}`}>
              <h3>{uni.emoji} {uni.name} Faculty</h3>
              <div className="results-stats">
                <p>
                  <strong>{searchInfo.total?.toLocaleString() || results.length} researchers found</strong>
                  {searchInfo.totalWorks > 0 && ` • from ${searchInfo.totalWorks.toLocaleString()} works`}
                  {searchInfo.withOrcid > 0 && ` • ${searchInfo.withOrcid} with ORCID`}
                </p>
                <p style={{fontSize: '14px', color: '#718096', marginTop: '4px'}}>
                  ✨ {searchInfo.searchMethod}
                </p>
                {searchInfo.keyword && searchInfo.keyword !== 'all' && (
                  <p style={{fontSize: '13px', color: '#4a5568'}}>
                    🔍 Filtered by: "{searchInfo.keyword}"
                    {(yearFrom || yearTo) && ` • 📅 ${yearFrom || '...'} – ${yearTo || '...'}`}
                  </p>
                )}
                {(!searchInfo.keyword || searchInfo.keyword === 'all') && (yearFrom || yearTo) && (
                  <p style={{fontSize: '13px', color: '#4a5568'}}>
                    📅 Year filter: {yearFrom || '...'} – {yearTo || '...'}
                  </p>
                )}
              </div>
            </div>
            
            <div className="people-grid">
              {activeTab === "berkeley" ? (
                // Berkeley has a special card with ORCID + website links
                results.map((profile, i) => (
                  <div key={profile.orcidId || `${profile.dataSource}-${i}`} className={`person-card ${profile.orcidId ? 'orcid-person' : 'no-orcid'}`}>
                    <div className="card-header">
                      <h4>
                        {profile.fullName || profile.name}
                        {profile.orcidId && (
                          <span style={{fontSize: '14px', color: '#059669', marginLeft: '8px'}}>✓ ORCID</span>
                        )}
                        {profile.dataSource?.includes('berkeley') && (
                          <span style={{fontSize: '14px', color: '#003262', marginLeft: '8px'}}>✓ Berkeley</span>
                        )}
                      </h4>
                      {user.role === "admin" && profile.orcidId && (
                        <button onClick={() => saveOrcidProfile(profile)} className="save-profile-btn" title="Add to local directory">
                          💾 Save
                        </button>
                      )}
                    </div>
                    <div className="person-details">
                      {profile.profileUrl && !profile.profileUrl.includes('orcid.org') && (
                        <p style={{marginBottom: '10px'}}>
                          <strong>🏫 Berkeley Profile:</strong>{' '}
                          <a href={profile.profileUrl} target="_blank" rel="noopener noreferrer"
                            style={{color: '#003262', textDecoration: 'underline'}}>View on Berkeley →</a>
                        </p>
                      )}
                      {profile.orcidId ? (
                        <div style={{marginBottom: '10px'}}>
                          <p style={{marginBottom: '5px'}}>
                            <strong>🆔 ORCID:</strong>{' '}
                            <a href={`https://orcid.org/${profile.orcidId}`} target="_blank" rel="noopener noreferrer"
                              style={{color: '#a6ce39', textDecoration: 'underline'}}>{profile.orcidId}</a>
                          </p>
                        </div>
                      ) : (
                        <p style={{color: '#b45309', marginBottom: '10px'}}>
                          <strong>ℹ️ ORCID profile not linked</strong>
                        </p>
                      )}
                      {profile.department && <p><strong>🏛️ Department:</strong> {profile.department}</p>}
                      <p><strong>🎓 Affiliation:</strong> University of California, Berkeley</p>
                      {profile.keywords && profile.keywords.length > 0 && (
                        <div style={{marginTop: '8px'}}>
                          <strong>🔬 Research Interests:</strong>
                          <p style={{marginTop: '4px', fontSize: '14px', color: '#4a5568'}}>
                            {profile.keywords.join(', ')}
                          </p>
                        </div>
                      )}
                      {profile.dataSource && (
                        <p style={{fontSize: '12px', color: '#718096', marginTop: '8px', padding: '4px 8px', background: '#f3f4f6', borderRadius: '4px', display: 'inline-block'}}>
                          📍 Source: {profile.dataSource === 'berkeley_website_and_orcid' ? 'Berkeley Website + ORCID' : 
                                     profile.dataSource === 'berkeley_website' ? 'Berkeley Website' :
                                     profile.dataSource === 'orcid_only' ? 'ORCID Only' :
                                     profile.dataSource.replace(/_/g, ' ')}
                        </p>
                      )}
                      {(profile.biography || profile.bio || profile.orcidBio) && (
                        <div className="biography">
                          <strong>📝 Biography:</strong>
                          <p className="biography-text">
                            {((profile.biography || profile.bio || profile.orcidBio || '').length > 200 
                              ? `${(profile.biography || profile.bio || profile.orcidBio).substring(0, 200)}...` 
                              : (profile.biography || profile.bio || profile.orcidBio))}
                          </p>
                        </div>
                      )}
                      {profile.works && profile.works.length > 0 && (
                        <div className="works-section">
                          <strong>📚 Recent Works ({profile.works.length}):</strong>
                          <ul className="works-list">
                            {profile.works.slice(0, 3).map((work, idx) => (
                              <li key={idx} className="work-item">
                                <span className="work-title">{work.title}</span>
                                {work.journal && <span className="work-journal"> • {work.journal}</span>}
                                {(work.publicationDate || work.year) && <span className="work-year"> ({work.publicationDate || work.year})</span>}
                                {work.doi && (
                                  <a href={`https://doi.org/${work.doi}`} target="_blank" rel="noopener noreferrer" className="doi-link">📄 DOI</a>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                // OpenAlex cards for Imperial, ANU, Wits, UBC
                results.map((profile, i) => (
                  <OpenAlexProfileCard
                    key={profile.openAlexId || profile.orcidId || `openalex-${i}`}
                    profile={profile}
                    uni={uni}
                    user={user}
                    onSave={saveOrcidProfile}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* Local Results */}
        {activeTab === "local" && localResults.length > 0 && (
          <div className="results-section">
            <h3>Local Directory Results ({localResults.length})</h3>
            <div className="people-grid">
              {localResults.map((person, i) => {
                const profileLinkMatch = person.summary?.match(/Profile: (https?:\/\/[^\s]+)/);
                const profileLink = profileLinkMatch ? profileLinkMatch[1] : null;
                const titleMatch = person.summary?.match(/^(Prof\.|Dr\.?|Professor|Doctor)/);
                const title = titleMatch ? titleMatch[1] : null;
                
                return (
                  <div key={i} className="person-card local-person">
                    <div className="card-header">
                      <h4>
                        {title && <span className="person-title">{title} </span>}
                        {person.name}
                      </h4>
                    </div>
                    <div className="person-details">
                      <p><strong>🏛️ Department:</strong> {person.department || "Not specified"}</p>
                      {person.email && person.email.trim() && (
                        <p><strong>📧 Email:</strong>{" "}
                          <a href={`mailto:${person.email}`} className="email-link">{person.email}</a>
                        </p>
                      )}
                      {person.skills && person.skills.length > 0 && (
                        <p><strong>🔧 Skills:</strong> {person.skills.join(", ")}</p>
                      )}
                      {profileLink && (
                        <p><strong>🔗 Profile:</strong>{" "}
                          <a href={profileLink} target="_blank" rel="noopener noreferrer" className="profile-link">View Profile</a>
                        </p>
                      )}
                      {person.summary && (
                        <div className="summary">
                          <strong>📝 Summary:</strong>
                          <p className="summary-text">{person.summary}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No results messages */}
        {searchTerm && !loading && (
          <>
            {activeTab !== "local" && results.length === 0 && (
              <p className="no-results">No {uni?.name} researchers found matching "{searchTerm}".</p>
            )}
            {activeTab === "local" && localResults.length === 0 && (
              <p className="no-results">No people found in local directory.</p>
            )}
          </>
        )}

        {/* Search help */}
        {!searchTerm && !loading && (
          <div className="search-help">
            <h3>🔍 How to Search</h3>
            <div className="search-tips">
              {Object.entries(UNIVERSITIES).map(([key, u]) => (
                <div key={key} className="tip">
                  <strong>{u.emoji} {u.name}:</strong>
                  <p>Search {u.name} researchers by name, department, or research keywords via {u.source === "openalex" ? "OpenAlex" : "ORCID + Website"}.</p>
                </div>
              ))}
              <div className="tip">
                <strong>🏠 Local Directory:</strong>
                <p>Search your organization's saved researcher profiles and contacts.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
